/**
 * CSV Parser & Transformer
 * Transforms raw CSV data into patient records with validation
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { detectColumns, hasPatientIdentifier, getDetectionSummary } from './columnDetection';

dayjs.extend(customParseFormat);

// Date formats to try (ordered by likelihood)
const DATE_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'MMM D, YYYY',
  'MMMM D, YYYY',
  'D MMM YYYY',
  'MM/DD/YY',
  'M/D/YY',
];

/**
 * Parse a date string with flexible format detection
 */
function parseDate(value) {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Try each format with strict mode — no permissive fallback
  for (const format of DATE_FORMATS) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) {
      return parsed.toISOString();
    }
  }

  // Try ISO 8601 explicitly (e.g. "2024-01-15T00:00:00.000Z")
  const iso = dayjs(trimmed, 'YYYY-MM-DDTHH:mm:ss', true);
  if (iso.isValid()) {
    return iso.toISOString();
  }

  return null;
}

/**
 * Normalize phone number to digits only
 */
function normalizePhone(value) {
  if (!value || typeof value !== 'string') return null;

  const digits = value.replace(/\D/g, '');

  // Must have at least 10 digits for a valid US phone
  if (digits.length < 10) return null;

  // Return last 10 digits (strips country code if present)
  return digits.slice(-10);
}

/**
 * Check if a value indicates "Yes" / true
 */
function isYes(value) {
  if (!value) return false;
  const lower = String(value).toLowerCase().trim();
  return ['yes', 'true', '1', 'y'].includes(lower);
}

/**
 * Check if a value indicates "No" / false
 */
function isNo(value) {
  if (!value) return false;
  const lower = String(value).toLowerCase().trim();
  return ['no', 'false', '0', 'n'].includes(lower);
}

/**
 * Transform a single CSV row into a patient record
 */
function transformRow(row, columnMapping) {
  const mapped = {};

  // Extract values based on column mapping
  for (const [csvHeader, field] of Object.entries(columnMapping)) {
    if (field && row[csvHeader] !== undefined) {
      mapped[field] = row[csvHeader];
    }
  }

  // Build patient name
  let name = null;
  if (mapped.name && mapped.name.trim()) {
    name = mapped.name.trim();
  } else if (mapped.firstName || mapped.lastName) {
    name = [mapped.firstName, mapped.lastName].filter(Boolean).join(' ').trim();
  }

  // Build contact info
  const phone = normalizePhone(mapped.phone);
  const email = mapped.email?.trim() || null;

  // Parse dates
  const dob = parseDate(mapped.dob);
  const lastVisit = parseDate(mapped.lastVisit);
  const nextAppt = parseDate(mapped.nextVisit);
  const lastPsaDate = parseDate(mapped.lastPsaDate);
  const lastTtDate = parseDate(mapped.lastTtDate);

  // Check deceased status
  const isDeceased = isYes(mapped.deceased);

  // Check SMS consent
  const canSms = !isNo(mapped.consentToText); // Default to true unless explicitly "No"

  return {
    // Identity
    name,
    mrn: mapped.mrn?.trim() || null,

    // Contact
    phone,
    email,

    // Clinical
    dob,
    last_touch: lastVisit,
    diagnosis: mapped.diagnosis?.trim() || null,
    notes: mapped.notes?.trim() || null,
    callouts: mapped.callouts?.trim() || null,
    status: mapped.status?.trim() || 'new',
    provider: mapped.provider?.trim() || null,
    insurance: mapped.insurance?.trim() || null,
    medications: mapped.medications?.trim() || null,

    // EHR clinical details (for flag translation)
    next_appt: nextAppt,
    last_psa_date: lastPsaDate,
    last_psa_value: mapped.lastPsaValue?.trim() || null,
    last_psa_rises: mapped.lastPsaRises?.trim() || null,
    last_tt_date: lastTtDate,
    last_tt_value: mapped.lastTtValue?.trim() || null,
    imaging: mapped.imaging?.trim() || null,
    adt: mapped.adt?.trim() || null,
    disease_state: mapped.diseaseState?.trim() || null,

    // Flags
    is_deceased: isDeceased,
    can_sms: canSms,

    // Metadata (set during import)
    risk: null,
    risk_source: null,
  };
}

/**
 * Normalize a name for duplicate comparison:
 * lowercase, collapse whitespace, strip accents
 */
function normalizeName(name) {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generate duplicate detection keys for a patient.
 * Returns an array of keys — a patient matches if ANY key collides.
 */
function getDuplicateKeys(patient, clinicId) {
  const keys = [];
  const normalizedName = normalizeName(patient.name);

  // MRN is the strongest identifier
  if (patient.mrn) {
    keys.push(`mrn:${clinicId}:${patient.mrn.trim()}`);
  }

  // Name + DOB
  if (normalizedName && patient.dob) {
    keys.push(`name_dob:${clinicId}:${normalizedName}:${patient.dob}`);
  }

  // Name + Phone
  if (normalizedName && patient.phone) {
    keys.push(`name_phone:${clinicId}:${normalizedName}:${patient.phone}`);
  }

  // Name + Email
  if (normalizedName && patient.email) {
    keys.push(`name_email:${clinicId}:${normalizedName}:${patient.email.toLowerCase()}`);
  }

  return keys;
}

/**
 * Parse and validate CSV data
 * @param {Object[]} rows - Raw CSV rows from Papa Parse
 * @param {string[]} headers - CSV headers
 * @param {Object} existingPatients - Map of existing patients for duplicate detection
 * @param {string} clinicId - Clinic ID
 * @returns {Object} - Parsed result with patients, skipped, and stats
 */
export function parseCSV(rows, headers, existingPatients = {}, clinicId = null) {
  // Detect columns
  const columnMapping = detectColumns(headers);
  const summary = getDetectionSummary(columnMapping);

  // Check for patient identifier
  if (!hasPatientIdentifier(columnMapping)) {
    return {
      success: false,
      error: 'NO_PATIENT_IDENTIFIER',
      message: 'We couldn\'t find patient names in your file. Expected columns like: Patient, Name, FirstName, or MRN.',
      columnMapping,
      summary,
    };
  }

  const patients = [];
  const skipped = {
    noName: [],
    deceased: [],
    duplicate: [],
  };

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const transformed = transformRow(row, columnMapping);

    // Skip if no name and no MRN
    if (!transformed.name && !transformed.mrn) {
      skipped.noName.push({ rowIndex: i + 1, row });
      continue;
    }

    // Use MRN as name fallback
    if (!transformed.name && transformed.mrn) {
      transformed.name = `Patient ${transformed.mrn}`;
    }

    // Skip deceased patients
    if (transformed.is_deceased) {
      skipped.deceased.push({ rowIndex: i + 1, row, name: transformed.name });
      continue;
    }

    // Check for duplicates (cascade: MRN → Name+DOB → Name+Phone → Name+Email)
    const dupKeys = getDuplicateKeys(transformed, clinicId);
    const matchedKey = dupKeys.find((k) => existingPatients[k]);
    if (matchedKey) {
      skipped.duplicate.push({
        rowIndex: i + 1,
        row,
        name: transformed.name,
        matchedOn: matchedKey.split(':')[0],
      });
      continue;
    }

    // Add clinic_id
    transformed.clinic_id = clinicId;

    // Remove is_deceased flag (we've already filtered)
    delete transformed.is_deceased;

    patients.push(transformed);
  }

  // Calculate stats
  const stats = {
    total: rows.length,
    imported: patients.length,
    skippedNoName: skipped.noName.length,
    skippedDeceased: skipped.deceased.length,
    skippedDuplicate: skipped.duplicate.length,
    missingContact: patients.filter(p => !p.phone && !p.email).length,
    missingVisitDate: patients.filter(p => !p.last_touch).length,
    hasCallouts: patients.filter(p => p.callouts).length,
    hasInsurance: patients.filter(p => p.insurance).length,
    hasMedications: patients.filter(p => p.medications).length,
  };

  return {
    success: true,
    patients,
    skipped,
    stats,
    columnMapping,
    summary,
  };
}

/**
 * Build existing patients map for duplicate detection
 */
export function buildExistingPatientsMap(patients, clinicId) {
  const map = {};

  for (const patient of patients) {
    const normalized = normalizeName(patient.name);

    // Add MRN key
    if (patient.mrn) {
      map[`mrn:${clinicId}:${patient.mrn.trim()}`] = patient;
    }

    // Add Name + DOB key
    if (normalized && patient.dob) {
      map[`name_dob:${clinicId}:${normalized}:${patient.dob}`] = patient;
    }

    // Add Name + Phone key
    if (normalized && patient.phone) {
      map[`name_phone:${clinicId}:${normalized}:${patient.phone}`] = patient;
    }

    // Add Name + Email key
    if (normalized && patient.email) {
      map[`name_email:${clinicId}:${normalized}:${patient.email.toLowerCase()}`] = patient;
    }
  }

  return map;
}

/**
 * Get preview data (first 3 valid rows)
 */
export function getPreviewRows(patients, count = 3) {
  return patients.slice(0, count).map(p => ({
    name: p.name,
    contact: p.phone || p.email || 'No contact',
    lastVisit: p.last_touch ? dayjs(p.last_touch).format('MMM D, YYYY') : 'No date',
  }));
}
